import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GetStorageParams } from "./storage.types";
import { STORAGE_KEYS } from "./storage.keys";
import { storageServices } from "./storage.services";
import {
  getClientStorageSettings,
  updateClientStorageSettings,
  calculateStorageForMonth,
  getStorageCalculations,
  processMonthlyStorageInvoices,
  createDailySnapshot,
  getJobsStatus,
  triggerDailySnapshotsJob,
  triggerMonthlyInvoicesJob,
  triggerInvoiceSyncJob,
  triggerStorageCalculationsJob,
  ClientStorageSettings,
  ProcessStorageInvoicesRequest,
  // Storage Invoices
  getStorageInvoices,
  getStorageInvoiceById,
  createStorageInvoice,
  updateStorageInvoice,
  deleteStorageInvoice,
  markStorageInvoiceAsSent,
  markStorageInvoiceAsPaid,
  GetStorageInvoicesParams,
  CreateStorageInvoiceRequest,
  StorageInvoiceStatus,
} from "./storage.api";

export const useGetStorages = (params?: GetStorageParams) => {
  return useQuery({
    queryKey: STORAGE_KEYS.GET_STORAGES(params),
    queryFn: () => storageServices.getStorages(params),
  });
};

// ========================= STORAGE SETTINGS QUERIES =========================

export const useClientStorageSettings = (clientId: string) => {
  return useQuery({
    queryKey: STORAGE_KEYS.clientSettings(clientId),
    queryFn: () => getClientStorageSettings(clientId),
    enabled: !!clientId,
  });
};

export const useUpdateClientStorageSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      settings,
    }: {
      clientId: string;
      settings: Partial<ClientStorageSettings>;
    }) => updateClientStorageSettings(clientId, settings),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.clientSettings(clientId),
      });
    },
  });
};

// ========================= STORAGE CALCULATIONS QUERIES =========================

export const useStorageCalculations = (filters: {
  clientId?: string;
  year?: number;
  month?: number;
} = {}) => {
  return useQuery({
    queryKey: STORAGE_KEYS.calculationsList(filters),
    queryFn: () => getStorageCalculations(filters.clientId, filters.year, filters.month),
  });
};

export const useCalculateStorageForMonth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      clientId: string;
      year: number;
      month: number;
    }) => calculateStorageForMonth(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

export const useProcessMonthlyStorageInvoices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request?: ProcessStorageInvoicesRequest) =>
      processMonthlyStorageInvoices(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

export const useCreateDailySnapshot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, date }: { clientId: string; date?: string }) =>
      createDailySnapshot(clientId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

// ========================= JOB MANAGEMENT QUERIES =========================

export const useJobsStatus = () => {
  return useQuery({
    queryKey: STORAGE_KEYS.jobsStatus(),
    queryFn: getJobsStatus,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useTriggerDailySnapshotsJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerDailySnapshotsJob,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.jobsStatus(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

export const useTriggerMonthlyInvoicesJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerMonthlyInvoicesJob,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.jobsStatus(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

export const useTriggerInvoiceSyncJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerInvoiceSyncJob,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.jobsStatus(),
      });
    },
  });
};

export const useTriggerStorageCalculationsJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerStorageCalculationsJob,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.jobsStatus(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.calculations(),
      });
    },
  });
};

// ========================= STORAGE INVOICES QUERIES =========================

export const useStorageInvoices = (params: GetStorageInvoicesParams = {}) => {
  return useQuery({
    queryKey: STORAGE_KEYS.invoicesList(params),
    queryFn: () => getStorageInvoices(params),
  });
};

export const useStorageInvoiceById = (id: string) => {
  return useQuery({
    queryKey: STORAGE_KEYS.invoiceDetail(id),
    queryFn: () => getStorageInvoiceById(id),
    enabled: !!id,
  });
};

export const useCreateStorageInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateStorageInvoiceRequest) => createStorageInvoice(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoices(),
      });
    },
  });
};

export const useUpdateStorageInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateStorageInvoiceRequest> & { status?: StorageInvoiceStatus } }) =>
      updateStorageInvoice(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoices(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoiceDetail(id),
      });
    },
  });
};

export const useDeleteStorageInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteStorageInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoices(),
      });
    },
  });
};

export const useMarkStorageInvoiceAsSent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => markStorageInvoiceAsSent(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoices(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoiceDetail(id),
      });
    },
  });
};

export const useMarkStorageInvoiceAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, paidAmount, paidDate }: { id: string; paidAmount?: number; paidDate?: string }) =>
      markStorageInvoiceAsPaid(id, paidAmount, paidDate),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoices(),
      });
      queryClient.invalidateQueries({
        queryKey: STORAGE_KEYS.invoiceDetail(id),
      });
    },
  });
};
